/**
 * The client for the bulk-import BFF, under /api/guides/imports.
 *
 * A module of thin functions rather than calls scattered through the pages, for the same reason
 * the guides list keeps its query shape in `guideListQuery`: the request bodies — the retry body in
 * particular, which the worker keys on field-by-field — are the kind of thing a test has to be able
 * to assert without mounting a page, and a drifted FormData field name fails silently (the server
 * stages nothing and the UI shows an empty batch). Everything here is `axios` against relative URLs,
 * so it works in the app and under the e2e network stub the same way the rest of the console does.
 */
import axios from 'axios';

export const IMPORTS_BASE = '/api/guides/imports';

/** The three guide kinds a batch can import. Mirrors TYPE_FILTERS in guideListQuery. */
export const IMPORT_PAGE_TYPES = ['skill-guide', 'hiring-guide', 'career-guide'] as const;
export type ImportPageType = (typeof IMPORT_PAGE_TYPES)[number];

/** Every status an import job can carry, in the order a summary shows them. */
export const JOB_STATUSES = [
  'queued',
  'running',
  'done',
  'failed',
  'skipped',
  'blocked',
  'canceled',
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

/** A job is still moving while it is queued or running; everything else is a resting state. */
export const ACTIVE_JOB_STATUSES: JobStatus[] = ['queued', 'running'];
/** The states a stuck or refused job can be re-run from. */
export const RETRYABLE_JOB_STATUSES: JobStatus[] = ['failed', 'blocked'];

/** A guide this file matched on a previous import, so the operator is not told it is new. */
export interface PriorImport {
  jobId: string;
  status: string;
  guideId?: string;
}

/** One file's outcome from staging: whether it was accepted and what was read out of it. */
export interface StageReport {
  fileName: string;
  ok: boolean;
  contentHash?: string;
  chars?: number;
  words?: number;
  imagesFailed?: number;
  priorImport?: PriorImport | null;
  error?: string;
}

/** The response from staging a group of files or a zip. `entries` is set only for a zip. */
export interface StageResult {
  batchId: string;
  staged: number;
  failed: number;
  jobs: number;
  reports: StageReport[];
  entries?: number;
}

export interface Batch {
  batchId: string;
  label?: string;
  pageType: string;
  createdBy?: string;
  createdAt?: string;
  canceledAt?: string | null;
}

export interface BatchesPage {
  content: Batch[];
  totalElements: number;
  page: number;
  size: number;
}

export interface StatusCount {
  status: string;
  count: number;
}

/** GET /batches/:id — the live counts and running cost for the dashboard. */
export interface BatchSummary {
  batchId: string;
  counts: StatusCount[];
  total: number;
  totalCostUsd: number;
}

export interface JobUsage {
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
}

export interface JobWarnings {
  uncoveredPoints?: string[];
  imageErrors?: string[];
  notes?: string[];
}

export interface ImportJob {
  _id: string;
  batchId: string;
  fileName: string;
  status: JobStatus;
  pageType: string;
  contentHash?: string;
  sourceKey?: string;
  sourceFileUrl?: string;
  guideId?: string;
  savedSlug?: string;
  attempts?: number;
  maxAttempts?: number;
  error?: string;
  usage?: JobUsage;
  warnings?: JobWarnings;
}

export interface JobsPage {
  content: ImportJob[];
  totalElements: number;
  page: number;
  size: number;
}

/** The body POST /jobs/:id/retry wants: the worker re-runs from the archived source these name. */
export interface RetryBody {
  batchId: string;
  pageType: string;
  fileName: string;
  sourceKey?: string;
  sourceFileUrl?: string;
  contentHash?: string;
}

/** The server caps a files upload at twenty; group a larger pick accordingly. */
export const FILES_PER_REQUEST = 20;

/** Create the batch the files then attach to. */
export async function createBatch(
  pageType: string,
  label?: string,
): Promise<{ batchId: string; pageType: string; label?: string }> {
  const { data } = await axios.post(`${IMPORTS_BASE}/batches`, { pageType, label });
  return data;
}

/**
 * Stage one group of up to twenty files onto a batch.
 *
 * FormData with the field name `files` and a `pageType` beside it, exactly as the worker reads it.
 * The field name is load-bearing: multer keys on it, so `file` or `documents` here stages nothing
 * and the batch looks empty with no error to explain why.
 */
export async function uploadFiles(
  batchId: string,
  files: File[],
  pageType: string,
): Promise<StageResult> {
  const form = new FormData();
  for (const file of files) form.append('files', file, file.name);
  form.append('pageType', pageType);
  const { data } = await axios.post(`${IMPORTS_BASE}/batches/${batchId}/files`, form);
  return data;
}

/** Stage a single zip onto a batch; the server expands it. Field name `file`, singular. */
export async function uploadZip(batchId: string, file: File): Promise<StageResult> {
  const form = new FormData();
  form.append('file', file, file.name);
  const { data } = await axios.post(`${IMPORTS_BASE}/batches/${batchId}/zip`, form);
  return data;
}

export async function listBatches(params: {
  page?: number;
  size?: number;
  createdBy?: string;
} = {}): Promise<BatchesPage> {
  const { data } = await axios.get(`${IMPORTS_BASE}/batches`, { params });
  return data;
}

export async function getBatchSummary(batchId: string): Promise<BatchSummary> {
  const { data } = await axios.get(`${IMPORTS_BASE}/batches/${batchId}`);
  return data;
}

export async function getBatchJobs(
  batchId: string,
  params: { status?: string; page?: number; size?: number } = {},
): Promise<JobsPage> {
  const { data } = await axios.get(`${IMPORTS_BASE}/batches/${batchId}/jobs`, { params });
  return data;
}

/** Re-run a failed or blocked job from its archived source. */
export async function retryJob(jobId: string, body: RetryBody): Promise<unknown> {
  const { data } = await axios.post(`${IMPORTS_BASE}/jobs/${jobId}/retry`, body);
  return data;
}

export async function cancelBatch(batchId: string): Promise<{ batchId: string; canceled: number }> {
  const { data } = await axios.post(`${IMPORTS_BASE}/batches/${batchId}/cancel`);
  return data;
}

/** Exactly the fields the retry endpoint wants, read off a job row. Pulled out so it is testable. */
export function retryBodyFor(job: ImportJob): RetryBody {
  return {
    batchId: job.batchId,
    pageType: job.pageType,
    fileName: job.fileName,
    sourceKey: job.sourceKey,
    sourceFileUrl: job.sourceFileUrl,
    contentHash: job.contentHash,
  };
}
